import Compressor from 'compressorjs';
import CryptoJS from 'crypto-js';
import { get_presigned_url } from '@/api/getData';

/**
 * Convert file to JPEG with 0.75 quality
 * @param {File} file
 * @returns {Promise<Blob>}
 */
const convertToJpeg = (file, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: quality,
      mimeType: 'image/jpeg',
      success(result) {
        resolve(result);
      },
      error(err) {
        reject(err);
      },
    });
  });
};

/**
 * Resize image (max dimension limit)
 * @param {File|Blob} file
 * @param {number} maxDimension
 * @returns {Promise<Blob>}
 */
const resizeImage = (file, maxDimension = 512) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      maxWidth: maxDimension,
      maxHeight: maxDimension,
      mimeType: 'image/jpeg',
      quality: 0.75, // Good enough for thumbnail
      success(result) {
        resolve(result);
      },
      error(err) {
        reject(err);
      },
    });
  });
};

/**
 * Calculate SHA1 hash of blob
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
const calculateSHA1 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const wordArray = CryptoJS.lib.WordArray.create(reader.result);
      const hash = CryptoJS.SHA1(wordArray).toString();
      resolve(hash);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Upload blob to S3 via presigned URL
 * @param {string} url
 * @param {Blob} blob
 * @returns {Promise<Response>}
 */
const uploadToS3 = (url, blob) => {
  return fetch(url, {
    method: 'PUT',
    body: blob,
    headers: {
      'Content-Type': 'image/jpeg',
    },
  });
};

const normalizeUrl = (url) => {
  if (!url) return url;
  return String(url).replace(/^\s*`+|`+\s*$/g, '').trim();
};

/**
 * Main upload handler
 * @param {File} file
 * @returns {Promise<string>} public_url
 */
export const handleS3Upload = async (file) => {
  try {
    // 1. Convert to JPEG & Compress
    const blobOrigin = await convertToJpeg(file, 0.75);

    // 2. Calculate SHA1
    const hash = await calculateSHA1(blobOrigin);
    const filename = `${hash}.jpg`;

    // 3. Generate Thumbnail
    const blobThumb = await resizeImage(blobOrigin, 512);

    // 4. Get Presigned URLs
    const res = await get_presigned_url({ filename });
    if (!res || !res.data) {
        throw new Error('Failed to get presigned URLs');
    }
    const upload_url_origin = normalizeUrl(res.data.upload_url_origin);
    const upload_url_thumb = normalizeUrl(res.data.upload_url_thumb);
    const public_url = normalizeUrl(res.data.public_url);

    // 5. Upload to S3 (Concurrent)
    await Promise.all([
      uploadToS3(upload_url_origin, blobOrigin),
      uploadToS3(upload_url_thumb, blobThumb),
    ]);

    return public_url;
  } catch (error) {
    console.error('S3 Upload Error:', error);
    throw error;
  }
};
